import { useEffect, useState } from "react";

import dayjs from "@shared/dayjs";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import { PencilSolidV6, TrashCanSolidV6, PlusSolidV6 } from "@shared/v2/Icomoon";

import ListingAlert from "../../PersonDetail/components/listing_alerts/ListingAlert";

import {
  getSettings,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type ListingAlertSettings,
  type ListingAlertTemplate,
} from "./service";
import AvatarLink from "../AvatarLink";
import TemplatesToolbar from "../TemplatesToolbar";
import DeleteTemplateModal from "../DeleteTemplateModal";
import useTemplateSearchParams from "../useTemplateSearchParams";

const THEAD_CLASSES = "tw-font-normal tw-py-8px";

const toSimplePerson = (fullName?: string | null, avatar?: string | null) =>
  fullName ? { fullName, avatar } : null;

interface ListingAlertFormParams {
  templateId?: number | null;
  templateName: string;
  listing_alert: unknown;
}

type SaveCallback = (result: { response: unknown }) => void;

const ListingAlertTemplates = () => {
  const { urlSearch, search, setSearch } = useTemplateSearchParams();
  const [settings, setSettings] = useState<ListingAlertSettings | null>(null);
  const [templates, setTemplates] = useState<ListingAlertTemplate[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const closeModals = () => {
    setSelectedTemplate(null);
    setShowModal(false);
    setShowDeleteConfirmationModal(false);
  };

  const editTemplate = (templateId: number) => {
    setSelectedTemplate(templateId);
    setShowModal(true);
  };

  const confirmDelete = (templateId: number) => {
    setSelectedTemplate(templateId);
    setShowDeleteConfirmationModal(true);
  };

  const reload = () => {
    closeModals();
    setRefreshKey((key) => key + 1);
  };

  const saveTemplate = async (
    templateId: number | null | undefined,
    templateName: string,
    alert: unknown,
    callback: SaveCallback,
  ) => {
    const response = templateId
      ? await updateTemplate(templateId, templateName, alert)
      : await createTemplate(templateName, alert);
    callback({ response });
    reload();
  };

  const templateProps = () => {
    if (!selectedTemplate) {
      return null;
    }

    const template = templates.find(({ id }) => id === selectedTemplate);
    if (!template) {
      return null;
    }

    return {
      templateId: selectedTemplate,
      templateName: template.name,
      ...template.query,
    };
  };

  useEffect(() => {
    let stale = false;
    void getSettings().then((data) => {
      if (!stale) setSettings(data);
    });
    return () => {
      stale = true;
    };
  }, []);

  const unavailable = settings?.unavailable ?? false;

  useEffect(() => {
    let stale = false;
    void getTemplates({ search: urlSearch }).then((data) => {
      if (!stale) setTemplates(Array.isArray(data) ? data : []);
    });
    return () => {
      stale = true;
    };
  }, [urlSearch, refreshKey]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-[16px] tw-flex-1 tw-min-h-0">
      {showModal && settings && (
        <ListingAlert
          person={{ id: settings.personId, home_app_last_login: Date.now() }}
          mlsIds={settings.mlsIds}
          closeListingAlertModal={closeModals}
          geoCodeAccountAddress={settings.geoCodeAccountAddress}
          type="new"
          handleFormSubmit={(params: ListingAlertFormParams, callback: SaveCallback) =>
            saveTemplate(params.templateId, params.templateName, params.listing_alert, callback)
          }
          alertInstructions="Enter the listing alert criteria you want to save. Anyone on your team will have access to this template."
          submitButton="SAVE TEMPLATE"
          title={selectedTemplate ? "Edit Listing Alert Template" : "New Listing Alert Template"}
          isCreatingTemplate
          {...(templateProps() ?? {})}
        />
      )}
      {showDeleteConfirmationModal && selectedTemplate !== null && (
        <ConfirmDeleteTemplate templateId={selectedTemplate} onClose={closeModals} onConfirmDelete={reload} />
      )}
      <TemplatesToolbar
        search={search}
        onSearchChange={setSearch}
        createLabel="Create New Listing Alert Template"
        onCreate={() => setShowModal(true)}
        createDisabled={!settings || unavailable}
      />
      {unavailable && (
        <div className="tw-text-gray-50">
          Listing alert search is temporarily unavailable. Please try again later.
        </div>
      )}
      {templates.length > 0 ? (
        <div className="tw-flex-1 tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto">
          <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
            <thead className="tw-bg-white tw-sticky tw-top-0">
              <tr className="tw-text-12px tw-text-gray-50 tw-border-1px tw-border-solid tw-border-t-0 tw-border-x-0 tw-border-gray-10">
                <th className={THEAD_CLASSES}>Title</th>
                <th className={THEAD_CLASSES}>Created</th>
                <th className={`tw-text-center ${THEAD_CLASSES}`}>Created By</th>
                <th className={THEAD_CLASSES}>Last Updated</th>
                <th className={`tw-text-center ${THEAD_CLASSES}`}>Last Updated By</th>
                <th className={`tw-text-center ${THEAD_CLASSES}`}></th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td className="tw-py-8px">{template.name}</td>
                  <td className="tw-py-8px">{dayjs(template.createdAt).format("MMM D, YYYY")}</td>
                  <td className="tw-py-8px tw-text-center">
                    <AvatarLink
                      simplePerson={toSimplePerson(template.createdByName, template.createdByAvatarUrl)}
                    />
                  </td>
                  <td className="tw-py-8px">{dayjs(template.updatedAt).format("MMM D, YYYY")}</td>
                  <td className="tw-py-8px tw-text-center">
                    <AvatarLink
                      simplePerson={toSimplePerson(template.updatedByName, template.updatedByAvatarUrl)}
                    />
                  </td>
                  <td className="tw-py-8px">
                    <div className="tw-flex tw-gap-[6px] tw-justify-end tw-items-center">
                      {/* @ts-expect-error props */}
                      <IconButton
                        disabled={!template.canWrite}
                        onClick={() => editTemplate(template.id)}
                        aria-label="Edit"
                        schema="tertiary"
                      >
                        <PencilSolidV6 />
                      </IconButton>
                      {/* @ts-expect-error props */}
                      <IconButton
                        disabled={!template.canWrite}
                        onClick={() => confirmDelete(template.id)}
                        aria-label="Delete"
                        schema="tertiary"
                      >
                        <TrashCanSolidV6 />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-mt-[66px] tw-mb-[50px] tw-items-center tw-justify-center tw-gap-[24px]">
          <span className="tw-text-18px tw-whitespace-nowrap">Create your first Listing Alert Template</span>
          <span className="tw-max-w-[750px] tw-text-gray-50 tw-text-center">
            Quickly fill in your frequently-used search areas and property details
          </span>
          {/* @ts-expect-error props */}
          <Button
            size="medium"
            disabled={unavailable}
            onClick={() => setShowModal(true)}
            className="tw-flex tw-justify-center tw-items-center tw-gap-6px"
          >
            <PlusSolidV6 />
            New Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default ListingAlertTemplates;

interface ConfirmDeleteTemplateProps {
  templateId: number;
  onClose: () => void;
  onConfirmDelete: () => void;
}

const ConfirmDeleteTemplate = ({ templateId, onClose, onConfirmDelete }: ConfirmDeleteTemplateProps) => {
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteTemplateWrapper = async () => {
    setIsFetching(true);
    setError(null);

    const { error: deleteError } = await deleteTemplate(templateId);

    if (deleteError) {
      setError(deleteError);
      setIsFetching(false);
    } else {
      onConfirmDelete();
    }
  };

  return (
    <DeleteTemplateModal
      show
      onClose={onClose}
      onConfirm={() => void deleteTemplateWrapper()}
      loading={isFetching}
      error={error}
    >
      <p className="tw-m-0">
        Are you sure you want to delete this Listing Alert Template? It will no longer be available for anyone
        on your team.
      </p>
    </DeleteTemplateModal>
  );
};
